import { Card } from "@/ui/components/primitives/card";
import { Button } from "@/ui/components/primitives/button";
import {
  ArrowDownToLine,
  ArrowUpFromLine,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import type { TransactionStats, FeeConfiguration } from "@/services/api/services/merchant-dashboard";

interface QuickActionsEnhancedProps {
  transactionStats: TransactionStats;
  merchantName: string;
  successRate: number;
  feeConfiguration?: FeeConfiguration;
}

export function QuickActionsEnhanced({
  transactionStats,
  merchantName,
  successRate,
  feeConfiguration
}: QuickActionsEnhancedProps) {
  const navigate = useNavigate();

  // Provide defaults for undefined props
  const stats = transactionStats || {
    totalTransactions: 0,
    averageTransactionValue: 0
  };
  const name = merchantName || 'Merchant';
  const fees = feeConfiguration || {
    payin: { percentage: 0, fixed: 0 },
    payout: { percentage: 0, fixed: 0 }
  };

  const handlePayinClick = () => {
    navigate("/create-payin");
  };

  const handlePayoutClick = () => {
    navigate("/create-payout");
  };

  return (
    <Card className="p-4 sm:p-6">
      <div className="mb-6">
        <h3 className="text-lg font-semibold">Quick Actions</h3>
        <p className="text-sm text-muted-foreground mt-1">
          Create new transactions
        </p>
      </div>

      <div className="space-y-3 sm:space-y-4">
        <Button
          className="w-full justify-start gap-2 sm:gap-3 h-auto py-3 sm:py-4 min-w-0"
          onClick={handlePayinClick}
        >
          <div className="h-8 w-8 sm:h-10 sm:w-10 rounded-lg bg-primary-foreground/10 flex items-center justify-center shrink-0">
            <ArrowDownToLine className="h-4 w-4 sm:h-5 sm:w-5" strokeWidth={1.5} />
          </div>
          <div className="text-left min-w-0 flex-1">
            <p className="font-semibold text-sm sm:text-base truncate">Create Collection</p>
            <p className="text-xs text-primary-foreground/80 font-normal truncate">
              Generate collection link
            </p>
          </div>
        </Button>

        <Button
          variant="outline"
          className="w-full justify-start gap-2 sm:gap-3 h-auto py-3 sm:py-4 min-w-0"
          onClick={handlePayoutClick}
        >
          <div className="h-8 w-8 sm:h-10 sm:w-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
            <ArrowUpFromLine className="h-4 w-4 sm:h-5 sm:w-5 text-primary" strokeWidth={1.5} />
          </div>
          <div className="text-left min-w-0 flex-1">
            <p className="font-semibold text-sm sm:text-base truncate">Create Payment</p>
            <p className="text-xs text-muted-foreground font-normal truncate">
              Transfer to beneficiary
            </p>
          </div>
        </Button>
      </div>

      <div className="mt-6 sm:mt-8 pt-4 sm:pt-6 border-t border-border">
        <div className="space-y-3 sm:space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-xs sm:text-sm text-muted-foreground">Merchant</span>
            <span className="text-xs sm:text-sm font-semibold">
              {name}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs sm:text-sm text-muted-foreground">Total Transactions Count</span>
            <span className="text-xs sm:text-sm font-semibold">
              {(stats.totalTransactions || 0).toLocaleString()}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs sm:text-sm text-muted-foreground">Success Rate</span>
            <span className="text-xs sm:text-sm font-semibold text-success">
              {successRate.toFixed(1)}%
            </span>
          </div>
          {(stats.averageTransactionValue || 0) > 0 && (
            <div className="flex items-center justify-between">
              <span className="text-xs sm:text-sm text-muted-foreground">Avg. Transaction Volume</span>
              <span className="text-xs sm:text-sm font-semibold">
                ₹{Math.round(stats.averageTransactionValue || 0).toLocaleString()}
              </span>
            </div>
          )}
          <div className="flex items-center justify-between">
            <span className="text-xs sm:text-sm text-muted-foreground">Payin Fee</span>
            <span className="text-xs sm:text-sm font-semibold">
              {fees.payin.percentage}%{fees.payin.fixed > 0 ? ` + ₹${fees.payin.fixed}` : ''}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs sm:text-sm text-muted-foreground">Payout Fee</span>
            <span className="text-xs sm:text-sm font-semibold">
              {fees.payout.percentage}%{fees.payout.fixed > 0 ? ` + ₹${fees.payout.fixed}` : ''}
            </span>
          </div>
        </div>
      </div>
    </Card>
  );
}